import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  isSettled,
  retrievalNeedsLeftOutcome,
  retrievalNeedsYard,
} from 'components/inventory/containers/configured-service/deactivation/settlement'
import { isCustomerOwned, isNoContainerPlacement, isPendingPlacement, isPlacement } from 'components/inventory/containers/utils'

import type { RetrievalResolution, SettleableRow } from 'components/inventory/containers/configured-service/deactivation/settlement'

export interface SettlementDefaults {
  // Where retrievals land unless the rep picks another yard.
  defaultYardId?: string
  // The date a retrieval is set for unless the rep gives a row its own — the service's end date.
  defaultDate?: Date | null
}

/**
 * One hook whether the outcomes are asked while deactivating or later. Placements and customer-owned
 * rows are stated, not asked: nobody delivers to an ended service, and their can is not ours to take.
 */
export const useContainerSettlement = <Row extends SettleableRow>(rows: Row[], { defaultYardId, defaultDate }: SettlementDefaults = {}) => {
  const [resolutions, setResolutions] = useState<Record<string, RetrievalResolution>>({})
  // Rows the rep dated themselves. They stop following the end date — one can going out Friday when
  // the service ends Monday is a deliberate answer.
  const [handDated, setHandDated] = useState<Set<string>>(() => new Set())

  // Read at set-time rather than closed over, so a row answered after the end date changed gets it.
  const defaultsRef = useRef({ defaultYardId, defaultDate })
  useEffect(() => {
    defaultsRef.current = { defaultYardId, defaultDate }
  })

  // Fill in what the answer implies: what comes back needs a destination, what does not has neither.
  const normalize = useCallback((resolution: RetrievalResolution): RetrievalResolution => {
    const next = { ...resolution }
    // Both scheduled and retrieved use the end date as a default — scheduled for when it will happen,
    // retrieved for when it did happen. Everything else has no date.
    if (next.kind === 'scheduled' || next.kind === 'retrieved') next.date = next.date ?? defaultsRef.current.defaultDate ?? null
    else delete next.date
    if (retrievalNeedsYard(next.kind)) next.yardId = next.yardId || defaultsRef.current.defaultYardId
    else delete next.yardId
    // No default for sold vs unassign: a guess there is a wrong number, not a missing one.
    if (!retrievalNeedsLeftOutcome(next.kind)) delete next.leftOutcome
    // The action is for creating a pickup WO; only scheduled removals get one.
    if (next.kind !== 'scheduled') delete next.actionId

    return next
  }, [])

  const placements = useMemo(() => rows.filter(isPlacement), [rows])
  const pendingPlacements = useMemo(() => rows.filter(isPendingPlacement), [rows])
  const noContainerRows = useMemo(() => rows.filter(isNoContainerPlacement), [rows])
  // The hauler's own containers — the only rows holding a retrieval decision.
  const real = useMemo(() => rows.filter(row => !isPlacement(row) && !isCustomerOwned(row)), [rows])
  // Theirs, and staying. Ownership crosscuts placement, so a customer-owned placement is not counted here.
  const customerOwned = useMemo(() => rows.filter(row => !isPlacement(row) && isCustomerOwned(row)), [rows])

  const set = useCallback(
    (id: string, patch: Partial<RetrievalResolution>) => {
      if (patch.date !== undefined) setHandDated(prev => new Set(prev).add(id))
      setResolutions(prev => ({ ...prev, [id]: normalize({ ...(prev[id] ?? { kind: 'scheduled' }), ...patch }) }))
    },
    [normalize]
  )

  // The end date IS the retrieval date in the common case, so changing it upstream carries through.
  const syncDefaultDate = useCallback(
    (date: Date | null) =>
      setResolutions(prev => {
        const next = { ...prev }
        Object.entries(prev).forEach(([id, resolution]) => {
          if (resolution.kind === 'scheduled' && !handDated.has(id)) next[id] = { ...resolution, date }
        })

        return next
      }),
    [handDated]
  )

  // Bulk fills only what is still unanswered: a control that can overwrite a considered answer is
  // one people stop trusting.
  const setAllUnsettled = useCallback(
    (resolution: RetrievalResolution) => {
      const filled = normalize(resolution)
      const touched = real.filter(row => !isSettled(resolutions[row.id])).map(row => row.id)
      setResolutions(prev => {
        const next = { ...prev }
        touched.forEach(id => {
          next[id] = { ...filled }
        })

        return next
      })
      if (filled.date) {
        setHandDated(prev => {
          const next = new Set(prev)
          touched.forEach(id => next.add(id))

          return next
        })
      }
    },
    [normalize, real, resolutions]
  )

  const settledCount = real.filter(row => isSettled(resolutions[row.id])).length

  const reset = useCallback(() => {
    setResolutions({})
    setHandDated(new Set())
  }, [])

  return {
    resolutions,
    set,
    setAllUnsettled,
    syncDefaultDate,
    reset,
    settled: settledCount === real.length,
    settledCount,
    placements,
    pendingPlacements,
    noContainerRows,
    real,
    customerOwned,
    defaultYardId,
  }
}

export type ContainerSettlement = ReturnType<typeof useContainerSettlement>
